import { Model, Validator, ValidatorWithoutProps } from '../types';

const tryParseURL = (value: string): URL | undefined => {
	try {
		return new URL(value);
	} catch {
		return undefined;
	}
};

export const required: ValidatorWithoutProps = value =>
	Array.isArray(value) ? value.length > 0 : Boolean(value);

export const or =
	<FieldNames extends string>(
		validatorA: Validator<FieldNames>,
		validatorB: Validator<FieldNames>,
	): Validator<FieldNames> =>
	(value, props) =>
		Boolean(validatorA(value, props) || validatorB(value, props));
export const and =
	<FieldNames extends string>(
		validatorA: Validator<FieldNames>,
		validatorB: Validator<FieldNames>,
	): Validator<FieldNames> =>
	(value, props) =>
		Boolean(validatorA(value, props) && validatorB(value, props));

export const type =
	<FieldNames extends string>(requiredType: 'string' | 'number'): Validator<FieldNames> =>
	value =>
		typeof value === requiredType;
export const isNull: ValidatorWithoutProps = value => value === null;

export const minLength =
	<FieldNames extends string>(requiredMinLength: number): Validator<FieldNames> =>
	value =>
		Boolean(value && value.length >= requiredMinLength);
export const maxLength =
	<FieldNames extends string>(requiredMaxLength: number): Validator<FieldNames> =>
	value =>
		Boolean(value && value.length <= requiredMaxLength);
export const maxLengthOrEmpty =
	<FieldNames extends string>(requiredMaxLength: number): Validator<FieldNames> =>
	value => {
		return !value ? true : Boolean(value.length <= requiredMaxLength);
	};
export const exactLength =
	<FieldNames extends string>(requiredExactLength: number): Validator<FieldNames> =>
	value =>
		Boolean(value && value.length === requiredExactLength);

export const email: ValidatorWithoutProps = value =>
	typeof value === 'string' &&
	Boolean(value.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/i));
// definitely not trying to validate every possible phone number format across the globe, just trying to catch things that are obviously wrong
export const phone: ValidatorWithoutProps = value =>
	typeof value === 'string' && Boolean(value.match(/^[\d+\-)( ]+$/i));
export const emptyOrValidPhone: ValidatorWithoutProps = value =>
	value === undefined || value === '' || value === null || phone(value);
export const currency: ValidatorWithoutProps = value =>
	typeof value === 'string' && Boolean(value.match(/^[1-9]\d*((,\d{3})*(\.\d{2})?)$/));
export const regexMatch =
	<FieldNames extends string>(regex: RegExp): Validator<FieldNames> =>
	value =>
		typeof value === 'string' && Boolean(value.match(regex));
export const digitsOnly: ValidatorWithoutProps = value =>
	typeof value === 'string' && Boolean(value.match(/^\d+$/));

export const greaterThan =
	<FieldNames extends string>(compare: number): Validator<FieldNames> =>
	value =>
		(typeof value === 'string' || typeof value === 'number') && parseFloat(value) > compare;
export const lessThan =
	<FieldNames extends string>(compare: number): Validator<FieldNames> =>
	value =>
		(typeof value === 'string' || typeof value === 'number') && parseFloat(value) < compare;

export const validDomain: ValidatorWithoutProps = value =>
	typeof value === 'string' && tryParseURL(`http://${value}`)?.hostname === value;
export const validURL: ValidatorWithoutProps = value => {
	if (typeof value !== 'string') {
		return false;
	}
	const parsed = tryParseURL(value);
	return Boolean(parsed && ['http:', 'https:'].includes(parsed.protocol));
};

export const validURLWithProtocolAndSlashes: ValidatorWithoutProps = value => {
	if (typeof value !== 'string') {
		return false;
	}
	if (!value.startsWith('http://') && !value.startsWith('https://')) {
		return false;
	}
	const parsedUrl = tryParseURL(value);
	if (!parsedUrl) {
		return false;
	}
	const validDomainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;

	return validDomainRegex.test(parsedUrl.hostname);
};

// isValid if empty
export const emptyOrValidURL: ValidatorWithoutProps = value => !value || validURL(value);

export const empty: ValidatorWithoutProps = value => !value;

// Make sure two values on the same form match
export const matchOtherValue =
	<FieldNames extends string>(fieldNameToMatch: FieldNames): Validator<FieldNames> =>
	(value, { model }) =>
		value === (model as Model<FieldNames> | Record<FieldNames, undefined>)[fieldNameToMatch];
